import { ChangeDetectionStrategy, Component, input, output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { NzTabsModule } from 'ng-zorro-antd/tabs';
import { ProductionBatch, Scan } from '../../../model';
import { AxisSpans } from './batch-analysis.component';
import { BatchPredicted3dPlotComponent } from './batch-predicted-3d-plot.component';
import { BatchPredictedAbPlotComponent } from './batch-predicted-AB-plot.component';

const LOG = '[PredictedPlots]';

/**
 * Tabbed container for predicted-LAB visualisations.
 *
 * Why two tabs instead of a single chart?
 *   - The 3-D L.A.B view gives the full tolerance geometry (sphere + box) but
 *     requires mouse rotation to understand.  Operators on the shop floor need
 *     a quick at-a-glance chroma view.
 *   - The a*-b* 2-D view collapses the lightness axis so chroma drift is
 *     immediately visible without any interaction.
 *
 * Tab layout:
 *   "AB"    → BatchPredictedAbPlotComponent  (2-D a* vs b* scatter)
 *   "L.A.B" → BatchPredicted3dPlotComponent  (full 3-D LAB scatter)
 *
 * All inputs are forwarded to both child plots so they share the same
 * tolerance data and axis spans.
 */
@Component({
  selector: 'app-batch-predicted-plots',
  imports: [
    CommonModule,
    NzTabsModule,
    BatchPredicted3dPlotComponent,
    BatchPredictedAbPlotComponent,
  ],
  template: `
    <nz-tabs nzSize="small">
      <!--
        Tab "AB": 2-D a*/b* chroma plane.
        Best for quick hue-drift inspection without 3-D rotation.
      -->
      <nz-tab nzTitle="AB">
        <app-batch-predicted-ab-plot
          [batch]="batch()"
          [axisSpans]="axisSpans()"
          [useDeltaE]="useDeltaE()"
          [useThresholds]="useThresholds()"
          (scanSelected)="onScanSelected($event)"
        ></app-batch-predicted-ab-plot>
      </nz-tab>

      <!--
        Tab "L.A.B": full 3-D scatter in LAB space.
        Shows the complete tolerance geometry including L* (lightness) axis.
      -->
      <nz-tab nzTitle="L.A.B">
        <app-batch-predicted-3d-plot
          [batch]="batch()"
          [axisSpans]="axisSpans()"
          [useDeltaE]="useDeltaE()"
          [useThresholds]="useThresholds()"
          (scanSelected)="onScanSelected($event)"
        ></app-batch-predicted-3d-plot>
      </nz-tab>
    </nz-tabs>
  `,
  styles: [`
    :host {
      display: block;
      width: 100%;
      border: 1px solid #d9d9d9;
    }
  `],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BatchPredictedPlotsComponent {
  /** The production batch whose scans are being visualised. */
  batch = input.required<ProductionBatch>();

  /**
   * Shared axis spans from the parent.
   * Forwarded to both child plots so the 2-D and 3-D charts use the same scale.
   */
  axisSpans = input<AxisSpans | null>(null);

  /**
   * Whether to overlay the ΔE tolerance circle/sphere.
   * Forwarded verbatim to both child plots.
   */
  useDeltaE = input<boolean>(true);

  /**
   * Whether to overlay the per-axis threshold rectangle/box.
   * Forwarded verbatim to both child plots.
   */
  useThresholds = input<boolean>(true);

  /** Emits whenever the user clicks a scan point in either tab. */
  scanSelected = output<Scan>();

  /**
   * Single handler shared by both child plots.
   * Logs which tab the click came from for debugging, then re-emits.
   */
  onScanSelected(scan: Scan) {
    console.debug(`${LOG} Scan selected id=${scan.id} seq=${scan.sequenceInBatch} state=${scan.state}`);
    this.scanSelected.emit(scan);
  }
}
